import { CalendarClock, CalendarX, Check, Lock, Video } from 'lucide-react';
import * as React from 'react';
import {
  currentPeriod,
  formatPlainDate,
  oneOnOneState,
  quarterBounds,
  today,
} from '@cfl/shared';

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
  toast,
} from '@/components/cargofl-ui';
import { PageContainer } from '@/components/shell/page-container';
import { PlainDatePicker, TimeSelect } from '@/components/plain-date-picker';
import {
  ONE_ON_ONE_STATE_LABELS,
  PILL_CLASS,
  oneOnOneStateVariant,
} from '@/lib/badges';
import {
  useAcknowledgeMeeting,
  useMarkMeetingNotHeld,
  useOneOnOnes,
  useSaveMeetingNotes,
  useScheduleOneOnOne,
  type OneOnOne,
} from '@/lib/engagement-api';
import { useMyReports } from '@/lib/people-api';
import { useCurrentUser } from '@/lib/session';

function err(e: unknown): string | undefined {
  return e instanceof Error ? e.message : undefined;
}

function MeetingCard({ meeting, meId }: { meeting: OneOnOne; meId: string }) {
  const isManager = meeting.managerId === meId;
  const isEmployee = meeting.employeeId === meId;
  const locked = meeting.acknowledgedAt !== null;
  // Derived, not stored - see `oneOnOneState`. `locked` stays its own thing: it
  // governs whether the notes may still be edited, which is only ever about
  // acknowledgement and must not start depending on the clock.
  const state = oneOnOneState(meeting);
  const notHeld = state === 'NOT_HELD';

  /**
   * The manager who booked it. Never the employee - acknowledgement is theirs alone
   * so nobody signs off a conversation on their behalf, and by the same logic nobody
   * erases one they were the other half of. The API refuses it for HR too.
   *
   * No `isHr` term, and that is not an oversight: `listOneOnOnes` only ever returns
   * meetings where the actor is the employee or the manager, so HR is never looking at
   * a third party's meeting on this screen. HR keeps the ability in the API for
   * closing out stale records; it has no surface here to use it from.
   */
  const canMarkNotHeld = isManager && !locked && !notHeld;
  const [marking, setMarking] = React.useState(false);
  const [reason, setReason] = React.useState('');
  const markNotHeld = useMarkMeetingNotHeld();

  const [notes, setNotes] = React.useState(meeting.notes ?? '');
  const save = useSaveMeetingNotes();
  const acknowledge = useAcknowledgeMeeting();

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-base">
            {isManager ? meeting.employee.name : meeting.manager.name}
          </CardTitle>
          <CardDescription>
            {new Date(meeting.scheduledAt).toLocaleString()} ·{' '}
            {isManager ? 'your report' : 'your manager'}
          </CardDescription>
          {meeting.meetLink && (
            // Shown as a link rather than a button: it leaves the app, and a
            // calendar invite for the same meeting is already in both inboxes.
            <a
              href={meeting.meetLink}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary underline-offset-4 hover:underline"
            >
              <Video className="size-3.5" />
              Join Google Meet
            </a>
          )}
        </div>
        <Badge variant={oneOnOneStateVariant(state)} className={PILL_CLASS}>
          {ONE_ON_ONE_STATE_LABELS[state]}
        </Badge>
      </CardHeader>

      <CardContent className="space-y-3">
        {meeting.agenda && (
          <div>
            <Label className="text-xs">Agenda</Label>
            <p className="text-sm text-muted-foreground">{meeting.agenda}</p>
          </div>
        )}

        {notHeld ? (
          <Alert>
            <CalendarX className="size-4" />
            <AlertDescription className="text-xs">
              Recorded as not held
              {meeting.notHeldBy ? ` by ${meeting.notHeldBy.name}` : ''} on{' '}
              {new Date(meeting.notHeldAt as string).toLocaleDateString()}
              {meeting.notHeldReason ? ` — "${meeting.notHeldReason}"` : ''}. Schedule another
              to hold the conversation.
            </AlertDescription>
          </Alert>
        ) : (
        <div className="space-y-1.5">
          <Label htmlFor={`notes-${meeting.id}`}>Meeting notes</Label>
          <Textarea
            id={`notes-${meeting.id}`}
            rows={3}
            value={locked ? (meeting.notes ?? '') : notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={!isManager || locked}
            placeholder={isManager ? 'What was discussed and agreed' : 'Your manager writes these'}
            className={locked ? 'pms-locked' : undefined}
          />
        </div>
        )}

        {locked && (
          <Alert>
            <Lock className="size-4" />
            <AlertDescription className="text-xs">
              Acknowledged {new Date(meeting.acknowledgedAt as string).toLocaleDateString()} — the
              notes are fixed. Editing after the fact would change what was agreed.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex gap-2">
          {canMarkNotHeld && (
            <Button hint={"Record that this meeting did not take place"}
              variant="ghost"
              className="gap-2 text-muted-foreground"
              onClick={() => setMarking(true)}
            >
              <CalendarX className="size-4" />
              Mark as not held
            </Button>
          )}
          {isManager && !notHeld && !locked && (
            <Button hint={"Save the meeting notes"}
              variant="outline"
              disabled={save.isPending}
              onClick={() => {
                void save
                  .mutateAsync({ id: meeting.id, notes })
                  .then(() => toast.success('Notes saved'))
                  .catch((e: unknown) => toast.error('Could not save', { description: err(e) }));
              }}
            >
              Save notes
            </Button>
          )}
          {isEmployee && !notHeld && !locked && meeting.notes && (
            <Button hint={"Confirm you have read the notes"}
              className="gap-2"
              disabled={acknowledge.isPending}
              onClick={() => {
                void acknowledge
                  .mutateAsync(meeting.id)
                  .then(() => toast.success('Acknowledged'))
                  .catch((e: unknown) =>
                    toast.error('Could not acknowledge', { description: err(e) }),
                  );
              }}
            >
              <Check className="size-4" />
              These notes are accurate
            </Button>
          )}
        </div>
      </CardContent>
      <Dialog open={marking} onOpenChange={(next) => !next && setMarking(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record this 1-on-1 as not held?</DialogTitle>
            <DialogDescription>
              It stops waiting for notes and stops counting as a conversation on record.
              {meeting.employee.name.split(' ')[0]} is notified, and this is recorded in the
              audit log under your name. It is not undone by clicking again — schedule another
              1-on-1 instead.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1000}
            aria-label="Why this 1-on-1 did not happen"
            placeholder="Why did it not happen? This is recorded in the audit log."
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarking(false)}>
              Cancel
            </Button>
            <Button hint={"Record that the meeting did not take place"}
              variant="destructive"
              disabled={reason.trim().length < 3 || markNotHeld.isPending}
              onClick={() => {
                void markNotHeld
                  .mutateAsync({ id: meeting.id, reason: reason.trim() })
                  .then(() => {
                    setMarking(false);
                    setReason('');
                    toast.success('Recorded as not held');
                  })
                  .catch((e: unknown) =>
                    toast.error('Could not record it', { description: err(e) }),
                  );
              }}
            >
              {markNotHeld.isPending ? 'Recording…' : 'Record as not held'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function MeetingsPage() {
  const me = useCurrentUser();
  const { data: meetings, isPending } = useOneOnOnes();
  // /people/my-reports, NOT /people: the latter is HR-only, so a manager loading
  // this page got a 403 and an empty "With" list - the 1-on-1 form could never be
  // filled in. The `as never` cast this replaces is what hid the mismatch.
  const { data: reports } = useMyReports(me.hasReports);
  const schedule = useScheduleOneOnOne();

  const [employeeId, setEmployeeId] = React.useState('');
  // Split into a date and a time so both can use the themed pickers. Recombined at
  // submit into the local-time string the API's instant is built from - the same
  // value the single datetime-local field produced.
  const [whenDate, setWhenDate] = React.useState('');
  const [whenTime, setWhenTime] = React.useState('10:00');
  const [agenda, setAgenda] = React.useState('');

  /**
   * The API accepts a meeting DATE inside Q4 only (the review conversation has to
   * happen before the final rating settles); HR is exempt so a missed one can still
   * be recorded. The form used to offer every date and let the server reject it,
   * which turned a known rule into a toast error after the fact. Feeding the window
   * to the native picker's min/max means an out-of-window date cannot be chosen at
   * all - no extra validation code, and the constraint is visible while choosing.
   */
  const isHrActor = me.role === 'HR' || me.role === 'SUPER_ADMIN';
  const q4 = quarterBounds(currentPeriod(today()).fyStartYear, 4);
  const windowMin = isHrActor ? undefined : q4.startsOn;
  const windowMax = isHrActor ? undefined : q4.endsOn;

  return (
    <PageContainer>
      {me.hasReports && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Schedule a 1-on-1</CardTitle>
            <CardDescription>
              Hold these before the final rating, not after — a rating that arrives without a
              conversation is a surprise, and a surprise is a grievance.{' '}
              {isHrActor ? (
                <>As HR you can record one on any date, including after the fact.</>
              ) : (
                <>
                  The meeting itself falls in Q4 ({formatPlainDate(q4.startsOn)} –{' '}
                  {formatPlainDate(q4.endsOn)}); you can book it any time before then.
                </>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                void schedule
                  .mutateAsync({
                    employeeId,
                    scheduledAt: new Date(`${whenDate}T${whenTime}`).toISOString(),
                    agenda: agenda || null,
                  })
                  .then(() => {
                    toast.success('1-on-1 scheduled');
                    setEmployeeId('');
                    setWhenDate('');
                    setWhenTime('10:00');
                    setAgenda('');
                  })
                  .catch((x: unknown) =>
                    toast.error('Could not schedule', { description: err(x) }),
                  );
              }}
            >
              <div className="space-y-1.5" data-guide="meeting-with">
                <Label htmlFor="meetingWith">With</Label>
                <Combobox
                  id="meetingWith"
                  className="w-[180px]"
                  value={employeeId || null}
                  // Select-only, and scoped to this manager's own reports — a 1-on-1
                  // is with someone who reports to you, which is a fact about the
                  // reporting tree, not something to type in.
                  onChange={setEmployeeId}
                  options={(reports ?? []).map((r) => ({
                    value: r.id,
                    label: r.name,
                    description: r.designationTitle ?? undefined,
                  }))}
                  searchPlaceholder="Search your reports"
                  emptyText="Nobody matches"
                />
              </div>

              <div className="space-y-1.5" data-guide="meeting-when">
                <Label htmlFor="when">When</Label>
                <div className="flex gap-2">
                  <PlainDatePicker
                    id="when"
                    value={whenDate}
                    onChange={setWhenDate}
                    className="w-[170px]"
                    min={windowMin}
                    max={windowMax}
                    placeholder="Pick a day"
                  />
                  <div className="w-[120px]">
                    <TimeSelect value={whenTime} onChange={setWhenTime} />
                  </div>
                </div>
              </div>

              <div className="min-w-[200px] flex-1 space-y-1.5">
                <Label htmlFor="agenda">Agenda</Label>
                <Input
                  id="agenda"
                  value={agenda}
                  onChange={(e) => setAgenda(e.target.value)}
                  placeholder="Optional"
                />
              </div>

              <Button hint={"Schedule a one-to-one"} data-guide="meeting-schedule" type="submit" disabled={!employeeId || !whenDate || schedule.isPending} className="gap-2">
                <CalendarClock className="size-4" />
                Schedule
              </Button>
              {(!employeeId || !whenDate) && <span className="self-center text-xs text-muted-foreground">{!employeeId ? 'Choose who the 1-on-1 is with' : 'Choose the date'}</span>}
            </form>
          </CardContent>
        </Card>
      )}

      {isPending && <Skeleton className="h-40 w-full" />}

      {!isPending && (meetings ?? []).length === 0 && (
        <Card>
          <CardContent className="py-14 text-center sm:py-14">
            <CalendarClock className="mx-auto mb-3 size-6 text-muted-foreground" />
            <p className="text-sm font-medium">No 1-on-1s yet</p>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {(meetings ?? []).map((m) => (
          <MeetingCard key={m.id} meeting={m} meId={me.id} />
        ))}
      </div>
    </PageContainer>
  );
}
